import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "@/components/seo";
import { Section } from "@/components/marketing/layout";
import { Button } from "@/components/ui/button";
import { SEO_PAGES } from "@/routes/seo";

export interface Post {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  read: string;
  body: ReactNode;
}

const A = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="font-medium text-brand-600 hover:underline">
    {children}
  </Link>
);

export const POSTS: Post[] = [
  {
    slug: "stop-whatsapp-feedback",
    title: "Why WhatsApp is the worst place to collect website feedback (and what to do instead)",
    excerpt: "Screenshots without context, voice notes without timestamps, and the eternal question: which page? A practical system for Indian agencies.",
    date: "2026-10-11",
    read: "6 min",
    body: (
      <>
        <p>
          Every web agency in India has lived this. You share the staging link on the client's WhatsApp group. Over the next three days you get eleven screenshots,
          four voice notes, a forwarded message from the client's cousin who "knows websites", and one call at 9:40 pm that starts with "bhai, ek chhota sa change
          hai". By Friday nobody can say which changes are done, which were cancelled, and which page the "make it bigger" note was even about.
        </p>
        <p>WhatsApp is brilliant for talking to clients. It is terrible as a system for website feedback. Here's why, and what works better.</p>

        <h2>The five problems with feedback on WhatsApp</h2>
        <h3>1. Screenshots lose the page</h3>
        <p>
          A cropped screenshot of a button doesn't say which page it came from, whether it was the mobile or desktop layout, or which browser showed it. Your
          developer spends the first ten minutes of every fix just finding the thing.
        </p>
        <h3>2. Voice notes can't be searched or ticked off</h3>
        <p>A two-minute voice note might contain five separate requests. Nobody can tick off half a voice note, and nobody re-listens to them a week later.</p>
        <h3>3. Requests get buried</h3>
        <p>
          A change request sent on Monday is forty messages up by Wednesday, below the photos of the new office and the "ok 👍" replies. Things get missed, and the
          client remembers that, not the forty things you did.
        </p>
        <h3>4. Too many people, no owner</h3>
        <p>
          Groups grow: the owner, the marketing manager, someone's nephew. When two people ask for opposite changes, there's no record of who decided what, and no
          clear person to approve the final version.
        </p>
        <h3>5. No proof of approval</h3>
        <p>
          When the final invoice goes out and the client says "but we never approved the homepage", scrolling back through a chat is not a great position to argue
          from.
        </p>

        <h2>What a good feedback system needs</h2>
        <p>You don't need a heavy project-management tool. You need five things:</p>
        <ul>
          <li>
            <strong>Feedback pinned to the exact spot</strong> on the exact page, so "this" always means something.
          </li>
          <li>
            <strong>Context captured automatically:</strong> page address, screen size, device and browser. Clients should never have to answer "which phone?".
          </li>
          <li>
            <strong>One list of changes</strong> with a status for each: open, in progress, waiting for the client, done.
          </li>
          <li>
            <strong>No logins for clients.</strong> If they have to create an account, they'll go back to WhatsApp.
          </li>
          <li>
            <strong>A written approval</strong> with a name and a date.
          </li>
        </ul>

        <h2>A simple process that works</h2>
        <ol>
          <li>
            <strong>Agree the rule at kick-off:</strong> "WhatsApp is for quick questions; changes go on the review link." Put it in your proposal, so it isn't
            awkward later.
          </li>
          <li>
            <strong>Send one review link per website</strong>, not a new message for every round.
          </li>
          <li>
            <strong>Let clients click on what they want changed</strong> and type the change there. One comment per change.
          </li>
          <li>
            <strong>Work through the list</strong>, moving each item to "awaiting review" when it's fixed so the client can check it.
          </li>
          <li>
            <strong>Ask for a formal approval</strong> once the list is empty, and keep it on record.
          </li>
        </ol>
        <p>
          You can still use WhatsApp for what it's good at: sending the review link, and nudging the client when changes are ready. Just don't let it be where
          the changes live.
        </p>

        <h2>How MarkFeed does this</h2>
        <p>
          We built MarkFeed for exactly this problem. You share a review link; the client opens it on their phone or laptop, taps the element they want changed and
          types the comment. MarkFeed saves a screenshot, the page, device and browser, and turns it into a task on your board. When everything's fixed, the
          client marks the page approved, and you get their name, the version and the time on record.
        </p>
        <p>
          Read more about <A to="/features/website-feedback">website feedback</A> and <A to="/features/client-feedback">client approvals</A>, or try it free on the
          Starter plan.
        </p>
      </>
    ),
  },
  {
    slug: "client-approval-checklist",
    title: "The client approval checklist every freelancer should send before the final invoice",
    excerpt: "How to get a clear written sign-off on a website — and avoid the ‘one last change’ loop after payment.",
    date: "2026-10-11",
    read: "5 min",
    body: (
      <>
        <p>
          The most expensive words in freelancing are "just one last change". They usually arrive after the site is live and before the final payment, and they
          keep arriving. The fix isn't to be less helpful. It's to make sign-off a clear, written step that everyone saw coming.
        </p>

        <h2>Before the project starts</h2>
        <ul>
          <li>
            <strong>Number of revision rounds.</strong> Two rounds of changes per page is common. Write the number in the proposal.
          </li>
          <li>
            <strong>Who approves.</strong> Name one person on the client's side. Others can comment; one person signs off.
          </li>
          <li>
            <strong>How feedback is sent.</strong> Agree on one channel for changes, like a review link, so nothing is lost in chats and calls.
          </li>
          <li>
            <strong>What counts as a change and what counts as new work.</strong> "Change the banner text" is a revision. "Add a careers section" is new work with a
            new quote.
          </li>
          <li>
            <strong>Payment milestones</strong> tied to approvals, for example 50% upfront, 30% on design approval, 20% on final approval.
          </li>
        </ul>

        <h2>The checklist to send before final approval</h2>
        <p>Send this to the approver when the last round of changes is done. Ask them to check each item on their own phone and laptop.</p>
        <ul>
          <li>All pages open and look right on mobile and desktop.</li>
          <li>Text, phone numbers, email addresses and the office address are correct.</li>
          <li>Logos, photos and colours are the final versions.</li>
          <li>Contact and enquiry forms send to the right inbox (send a test).</li>
          <li>WhatsApp, call and social media buttons open the right number or page.</li>
          <li>Prices, GST details and legal pages (privacy, terms, refunds) are correct, if the site has them.</li>
          <li>Page titles show the business name, so the site looks right on Google.</li>
          <li>Every change requested in earlier rounds is done or was dropped by agreement.</li>
        </ul>

        <h2>How to ask for the approval</h2>
        <p>Keep the message short and specific, for example:</p>
        <blockquote>
          "Hi Priya, all changes from round 2 are done. Please check the site against the attached checklist and approve it by Thursday. After approval, new
          changes will be quoted separately as per our agreement."
        </blockquote>
        <p>
          The deadline matters. Without one, approval drifts for weeks, and so does your payment. If the client doesn't reply, a polite reminder two days before
          the deadline usually does it.
        </p>

        <h2>Keep a record</h2>
        <p>
          An approval is only useful if you can show it later: who approved, which version, and when. A thumbs-up emoji in a group chat is easy to forget and hard
          to find.
        </p>
        <p>
          In MarkFeed, the client approves each website version with one click, choosing either "Everything looks good" or "Changes are required". MarkFeed
          records their name, the version and the time. If they ask for changes after approval, you can see exactly what was signed off, and quote the new work
          with confidence. See how <A to="/features/client-feedback">client approvals</A> work, or read our{" "}
          <A to="/for/freelancers">guide for freelancers</A>.
        </p>
      </>
    ),
  },
  {
    slug: "qa-on-staging",
    title: "Running stakeholder QA on staging without a spreadsheet",
    excerpt: "A lightweight review process for startup teams shipping weekly: versions, priorities and who signs off.",
    date: "2026-10-11",
    read: "7 min",
    body: (
      <>
        <p>
          Most startup teams run QA on staging the same way: someone creates a spreadsheet, shares it with the founders, product, design and marketing, and asks
          everyone to "add issues here". By the second release the sheet has 140 rows, half of them duplicates, and nobody knows which ones were fixed in which
          build.
        </p>
        <p>Here's a lighter process that works for teams shipping every week or two.</p>

        <h2>Why the spreadsheet breaks</h2>
        <ul>
          <li>
            <strong>No context.</strong> "Button overlaps on checkout" doesn't say which screen size, browser or account state.
          </li>
          <li>
            <strong>No link to the release.</strong> A row from two builds ago looks exactly like a new one.
          </li>
          <li>
            <strong>No clear status.</strong> "Fixed?" in a comment column isn't a workflow.
          </li>
          <li>
            <strong>Copying into the tracker.</strong> Someone has to re-type every row into Jira or Linear, and the two drift apart.
          </li>
        </ul>

        <h2>A better process in five steps</h2>
        <h3>1. Treat each staging release as a version</h3>
        <p>
          When a build is ready for review, give it a number (v12, or the release date) and tell reviewers which one they're looking at. Feedback then belongs to
          a version, and old issues don't get confused with new ones.
        </p>
        <h3>2. Give reviewers a focused brief</h3>
        <p>
          "Please review the new onboarding flow on mobile: sign-up, the first project, and the empty states" gets far better feedback than "have a look at
          staging". Name the flows and the devices.
        </p>
        <h3>3. Capture issues where they happen</h3>
        <p>
          Ask reviewers to report issues on the page itself, with the element, the page address, the browser and the screen size captured automatically. Engineers
          can then reproduce the issue without a follow-up chat.
        </p>
        <h3>4. Triage once a day</h3>
        <p>
          One person, usually the PM, sets a priority on each new issue (low, medium, high or critical), assigns it, and closes duplicates. Fifteen minutes a day
          is enough for most teams.
        </p>
        <h3>5. Close the loop with the reviewer</h3>
        <p>
          When an issue is fixed, move it to "awaiting review" so the person who reported it can confirm on the next build. Only then is it resolved. This one
          step stops most "I thought this was fixed" arguments.
        </p>

        <h2>Who signs off</h2>
        <p>
          Decide before the release who gives the final go-ahead: usually the PM for product flows, and design for visual changes. Write it in the release
          checklist. Everyone can report issues, but a release needs one clear yes.
        </p>

        <h2>Doing this in MarkFeed</h2>
        <p>
          MarkFeed is built for this loop. Add the MarkFeed script to staging (or share a review link), and reviewers click on any element to report an issue.
          Each report includes a screenshot, the page, the browser, the OS and the screen size. Issues land on a board with Open, In Progress, Awaiting Review and
          Resolved columns, with priorities, assignees and internal notes your reviewers don't see. Each website keeps its versions, and approvals are recorded
          per version.
        </p>
        <p>
          The script works on pages behind a login too, so reviewers can test signed-in flows. See <A to="/for/startups">MarkFeed for product teams</A> or the{" "}
          <A to="/features/task-management">task board</A>.
        </p>
      </>
    ),
  },
];

const fmt = (d: string) => new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "short", year: "numeric" }).format(new Date(d));

function PageSeo() {
  const { pathname } = useLocation();
  const seo = SEO_PAGES[pathname];
  return seo ? <Seo title={seo.title} description={seo.description} path={pathname} /> : null;
}

export function Blog() {
  return (
    <>
      <PageSeo />
      <Section page eyebrow="Blog" title="Notes on client feedback & agency operations" intro="Practical writing for Indian agencies, freelancers and product teams.">
        <div className="grid gap-4 md:grid-cols-3">
          {POSTS.map((p) => (
            <article key={p.slug} className="flex flex-col rounded-2xl border border-line bg-white p-6">
              <p className="text-xs text-slate-500">
                {fmt(p.date)} · {p.read} read
              </p>
              <h2 className="mt-3 text-lg font-semibold leading-snug">
                <Link to={`/blog/${p.slug}`} className="hover:text-brand-600">
                  {p.title}
                </Link>
              </h2>
              <p className="mt-2 flex-1 text-[15px] leading-relaxed text-slate-600">{p.excerpt}</p>
              <Link to={`/blog/${p.slug}`} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline">
                Read article <ArrowRight className="size-4" />
              </Link>
            </article>
          ))}
        </div>
      </Section>
    </>
  );
}

export function BlogPost({ post }: { post: Post }) {
  const others = POSTS.filter((p) => p.slug !== post.slug);
  return (
    <>
      <PageSeo />
      <article className="mx-auto max-w-3xl px-5 py-16 sm:py-20">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-ink">
          <ArrowLeft className="size-4" /> All articles
        </Link>
        <p className="mt-6 text-sm text-slate-500">
          {fmt(post.date)} · {post.read} read
        </p>
        <h1 className="mt-2 text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl">{post.title}</h1>
        <p className="mt-4 text-lg leading-relaxed text-slate-600">{post.excerpt}</p>
        <div className="mt-10 space-y-4 text-[17px] leading-relaxed text-slate-700 [&_blockquote]:border-l-4 [&_blockquote]:border-brand-600 [&_blockquote]:pl-4 [&_blockquote]:italic [&_h2]:mt-10 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:text-ink [&_h3]:mt-6 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-ink [&_li]:pl-1 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6">
          {post.body}
        </div>
        <div className="mt-14 rounded-2xl border border-line bg-white p-6 sm:p-8">
          <p className="text-lg font-semibold text-ink">Collect website feedback without the chaos</p>
          <p className="mt-1 text-slate-600">Clients click on the page, you get a task with a screenshot. Free plan, no card needed.</p>
          <Button asChild className="mt-4">
            <Link to="/signup">
              Start free <ArrowRight />
            </Link>
          </Button>
        </div>
        <h2 className="mt-14 text-lg font-semibold text-ink">More articles</h2>
        <ul className="mt-3 space-y-2">
          {others.map((p) => (
            <li key={p.slug}>
              <A to={`/blog/${p.slug}`}>{p.title}</A>
            </li>
          ))}
        </ul>
      </article>
    </>
  );
}
